// DTO ⇄ form values for Fulfill request — the only place its field names live.
import { FACILITY_KINDS, buildFacilityValue } from '@/config/formOptions';
import { formValue } from '@/lib/mappers/formValues';
import { unitSymbol } from '@/lib/mappers/units';
import { RETIRE_CREDITS } from '@/components/common';
import { processOutgoingShipmentDetail } from './outgoingShipmentsMappers';
import { EMPTY_FULFILL_SHIPMENT_FORM } from '../constants/fulfillShipment.constants';

// A shipment has nothing left to ship — only what it can still allocate.
const buildRemainingByUnit = (node) => {
  const remaining = {};
  if (node?.workingUnit) {
    remaining[unitSymbol(node.workingUnit)] = {
      toAllocate: node.remainingToAllocateQuantityWorking ?? null,
    };
  }
  if (node?.declarationUnit) {
    remaining[unitSymbol(node.declarationUnit)] = {
      toAllocate: node.remainingToAllocateQuantityDeclaration ?? null,
    };
  }
  return remaining;
};

// What is already allocated — counted UP by the inbounds picked, as remainingByUnit counts down.
const buildCommittedByUnit = (node) => {
  const committed = {};
  if (node?.workingUnit) {
    committed[unitSymbol(node.workingUnit)] = {
      allocated: node.allocatedQuantityWorking ?? null,
    };
  }
  if (node?.declarationUnit) {
    committed[unitSymbol(node.declarationUnit)] = {
      allocated: node.allocatedQuantityDeclaration ?? null,
    };
  }
  return committed;
};

// One `outgoingShipment(id)` node → the Fulfill request page's card and its locked fields.
export const processShipmentForFulfill = (node) => {
  const shipment = processOutgoingShipmentDetail(node);
  if (!shipment) return null;
  return {
    ...shipment,
    // A back-to-back facility has no terminals, so the field never renders for one.
    hasTerminal: Boolean(node?.terminalRefId),
    remainingByUnit: buildRemainingByUnit(node),
    committedByUnit: buildCommittedByUnit(node),
    // TODO: outgoingShipment has no transaction-date field yet — a dash until it does.
    transactionDate: '-',
    values: {
      ...EMPTY_FULFILL_SHIPMENT_FORM,
      facilityId: node?.site?.id
        ? buildFacilityValue(FACILITY_KINDS.site, node.site.id)
        : node?.backToBackAccountId
          ? buildFacilityValue(FACILITY_KINDS.backToBack, node.backToBackAccountId)
          : '',
      terminalId: formValue(node?.terminalRefId),
      vesselName: node?.vesselName ?? '',
    },
  };
};

// What this fulfilment assigns to the order — the credit drawn across every picked inbound.
const totalCertifiedQuantity = (batches = []) =>
  batches.reduce((sum, batch) => sum + (Number(batch.certifiedQuantity) || 0), 0);

// Form values → POST /:module/pending-orders/shipments/:id/fulfill.
export const buildFulfillShipmentDto = (values) => ({
  pendingOrderId: values.pendingOrderId ? Number(values.pendingOrderId) : undefined,
  // The order is being linked by this fulfilment, not already on the shipment.
  isNewOrderLink: true,
  terminalId: values.terminalId ? Number(values.terminalId) : undefined,
  vesselName: values.vesselName || undefined,
  retireCredits: values.retireCredits === RETIRE_CREDITS.yes,
  unitType: values.fulfillUnitType || undefined,
  // TODO: the route ignores both until it reads them as /dispatch does.
  assignedQuantity: totalCertifiedQuantity(values.batches) || undefined,
  assignedQuantityUnit: values.assignedQuantityUnit || undefined,
  // batchId is a Prisma Int and a GraphQL ID! is a string; transactionId stays alphanumeric text.
  batches: (values.batches ?? []).map((batch) => ({
    batchId: Number(batch.batchId),
    transactionId: batch.transactionId,
    certifiedQuantity: Number(batch.certifiedQuantity),
  })),
});
